import { MantineTab } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.tsx';
import { Divider, Flex, Tabs, TabsProps } from '@mantine/core';
import { PropsWithChildren, ReactNode } from 'react';
import { MantineTabProps } from './MantineTab.types';
import { StyledPanelsContainer, StyledTabsList } from './styled';

interface Props<T extends string> {
	defaultValue?: T;
	value?: T;
	tabs: MantineTabProps<T>[];
	panels: {
		value: T;
		content: JSX.Element;
	}[];
	skipTabsBorder?: boolean;
	skipPanelsBorder?: boolean;
	onlyDividerBorder?: boolean;
	onTabChange?: TabsProps['onChange'];
	rightContent?: ReactNode;
	keepMounted?: boolean;
}

export const MantineTabs = <T extends string>({
	defaultValue,
	value,
	tabs,
	panels,
	skipTabsBorder,
	skipPanelsBorder,
	onlyDividerBorder,
	onTabChange,
	children,
	rightContent,
	keepMounted,
}: PropsWithChildren<Props<T>>) => {
	return (
		<Tabs
			defaultValue={defaultValue}
			value={value}
			onChange={onTabChange}
			keepMounted={keepMounted}
			variant="pills"
			c="dark.4"
			color="blue.0"
			h="100%"
			w="100%"
			display="flex"
			style={{ flexDirection: 'column', overflow: 'hidden' }}
		>
			<Flex justify="space-between" align="center">
				<StyledTabsList $skipTabsBorder={onlyDividerBorder || skipTabsBorder}>
					{tabs.map((tab) => (
						<MantineTab key={`tab-${tab.value}`} {...tab} />
					))}
				</StyledTabsList>
				{rightContent}
			</Flex>

			{onlyDividerBorder && <Divider />}

			<StyledPanelsContainer
				direction="column"
				$skipPanelsBorder={onlyDividerBorder || skipPanelsBorder}
			>
				{children}

				{panels.map((panel) => (
					<Tabs.Panel value={panel.value} key={`panel-${panel.value}`} h="100%">
						{panel.content}
					</Tabs.Panel>
				))}
			</StyledPanelsContainer>
		</Tabs>
	);
};
